import axios from "const/axios";
import { useQuery } from "@tanstack/react-query";
import { API_URL } from "const/constants";

export const createAdmission = async (payload) => {
  const res = await axios.post(`${API_URL}/student`, payload);
  return res.data?.data ?? res.data;
};

export const patchApplicantDetails = async (id, payload) => {
  const res = await axios.patch(`${API_URL}/student/${id}/applicant-details`, payload);
  return res.data;
};

export const patchDocuments = async (id, attachments) => {
  const res = await axios.patch(`${API_URL}/student/${id}/documents`, { attachments });
  return res.data;
};

export const patchApplicationSection = async (id, section, payload) => {
  const res = await axios.patch(`${API_URL}/student/${id}/application/${section}`, payload);
  return res.data;
};

export const removeTermScheduleRow = async (id, rowId) => {
  const res = await axios.delete(
    `${API_URL}/student/${id}/application/term-schedule/${encodeURIComponent(rowId)}`
  );
  return res.data;
};

export const withdrawApplication = async (id, payload) => {
  const res = await axios.patch(`${API_URL}/student/${id}/application/withdraw`, payload);
  return res.data;
};

export const reactivateApplication = async (id) => {
  const res = await axios.patch(`${API_URL}/student/${id}/application/reactivate`);
  return res.data;
};

export const useStudentInstallments = (studentId) =>
  useQuery({
    queryKey: ["studentInstallments", studentId],
    queryFn: async () => {
      const res = await axios.get(`${API_URL}/admissions/student/${studentId}/installments`);
      return res.data?.data ?? [];
    },
    enabled: !!studentId,
    refetchOnWindowFocus: false,
    staleTime: 30_000,
  });

export const syncTermPayments = async (termId, payments) => {
  const res = await axios.post(`${API_URL}/admission-terms/${termId}/payments/sync`, payments);
  return res.data?.data ?? [];
};

export const getTermPayments = async (termId) => {
  const res = await axios.get(`${API_URL}/admission-terms/${termId}/payments`);
  return res.data?.data ?? [];
};

// Super-admin only (enforced server-side): permanently delete a term payment regardless of status.
// Used to fix a payment recorded against the wrong term. May reopen a fully-verified term.
export const deleteTermPaymentAsSuperAdmin = async (paymentId) => {
  const res = await axios.delete(`${API_URL}/admission-terms/payments/${paymentId}`);
  return res.data ?? null;
};

export const fetchAdmissionTerms = async (clientAccountDetailId) => {
  const res = await axios.get(
    `${API_URL}/admission-terms/admission-detail/${clientAccountDetailId}`
  );
  return res.data?.data ?? [];
};

export const useAdmissionTerms = (clientAccountDetailId) =>
  useQuery({
    queryKey: ["admissionTerms", clientAccountDetailId],
    queryFn: () => fetchAdmissionTerms(clientAccountDetailId),
    enabled: !!clientAccountDetailId,
    refetchOnWindowFocus: false,
    staleTime: 0,
  });

export const addInstallment = async (cadId, dto) => {
  const res = await axios.post(
    `${API_URL}/accounts/client-account-details/${cadId}/installments`,
    dto
  );
  return res.data?.data;
};

export const deleteInstallment = async (cadId, installmentId) => {
  await axios.delete(
    `${API_URL}/accounts/client-account-details/${cadId}/installments/${installmentId}`
  );
};

export const addTermPayment = (termId, payment) =>
  axios
    .post(`${API_URL}/admission-terms/${termId}/payments/add`, payment)
    .then((r) => r.data?.data ?? null);

export const resubmitTermPayment = (termId, paymentUuid, payment) =>
  axios
    .post(`${API_URL}/admission-terms/${termId}/payments/${paymentUuid}/resubmit`, payment ?? null)
    .then((r) => r.data?.data ?? null);

export const fetchCoeFeePayments = async (termId) => {
  const res = await axios.get(`${API_URL}/admission-terms/${termId}/coe-payments`);
  return res?.data?.data ?? [];
};

export const deleteCoeFeePayment = async (paymentId) => {
  const res = await axios.delete(`${API_URL}/admission-terms/coe-payments/${paymentId}`);
  return res?.data ?? null;
};

export const fetchAdmissionDeletePreview = async (studentId) => {
  const res = await axios.get(`${API_URL}/student/${studentId}/delete-preview`);
  return res.data?.data ?? null;
};

// Snapshot of everything that will be permanently removed when the admission is deleted,
// shown in the confirm dialog before the admin types DELETE.
export const useAdmissionDeletePreview = (studentId, enabled = true) =>
  useQuery({
    queryKey: ["admissionDeletePreview", studentId],
    queryFn: () => fetchAdmissionDeletePreview(studentId),
    enabled: !!studentId && enabled,
    refetchOnWindowFocus: false,
    staleTime: 0,
  });
